import React, { useState, MouseEvent } from "react";

// Component imports
import { FavouritedHeart, Image, PayoutBanner } from "@components";
import { Card, CardContent, Grid, Typography } from "@material";

// Misc Imports
import { useTranslation } from "@i18n";
import { makeStyles } from "@styles";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: 0,
    zIndex: 5000,
  },
  card: {
    position: "relative",
    width: "auto",
    borderRadius: 12,
    bottom: 0,
    height: 254,
    boxShadow: "inset 0 -1px 0 #E6E6E6",
    transform:
      "translateZ(0) scaleX(1) rotateX(0deg) rotateY(0deg) rotate(0deg) skew(0deg, 0deg)",
    transformStyle: "preserve-3d",
    transition:
      "transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    willChange: "transform, box-shadow",
    "&:hover": {
      cursor: "pointer",
      boxShadow: "inset 0 -1px 0 #E6E6E6, 0 50px 80px -20px rgba(0, 0, 0, 0.4)",
      transform:
        "translateZ(0) scale3d(1.05, 1.05, 1) rotateX(0deg) rotateY(0deg) rotate(0deg) skew(0deg, 0deg)",
      zIndex: 6,
    },
    "&:hover $brandImage": {
      filter: "brightness(110%)",
    },
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      marginRight: 0,
      height: 254,
    },
    [theme.breakpoints.down("md")]: { height: 254 },
  },
  cardPressed: {
    transform: "scale(0.97) !important",
    bottom: 0,
    boxShadow: "0 16px 32px -16px rgba(0, 0, 0, 0.3)",
    transitionProperty: "transform, box-shadow, bottom",
    transitionDuration: "0.12s",
  },
  cardContent: { padding: 0 },
  bottomContainer: {
    position: "relative",
    bottom: 24,
    width: "100%",
    border: `1px solid #efefef`,
    borderTop: 0,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  brandInfoContainer: {
    width: "100%",
    height: 212,
    marginBottom: 6,
    marginLeft: "auto",
    marginRight: "auto",
  },
  brandImageContainer: {
    width: "100%",
    height: 181,
    border: "1px solid #efefef",
    borderBottom: 0,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    boxSizing: "border-box",
    overflow: "hidden",
    zIndex: 3,
    [theme.breakpoints.down("md")]: { height: 181 },
  },
  brandImage: {
    height: "100%",
    width: "100%",
    objectFit: "cover",
    transition: "filter 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    willChange: "filter",
  },
  brandLogoRow: {
    marginBottom: 8,
    paddingLeft: 10,
  },
  brandLogoContainer: {
    height: 43,
    width: 43,
    borderRadius: 8,
    marginTop: 6,
    backgroundColor: "white",
    border: "1px solid #E6E6E6",
    zIndex: 4,
  },
  brandLogo: {
    margin: "auto",
    width: 35,
    height: 35,
  },
  brandNameContainer: {
    flex: "1 1 0",
    minWidth: 0,
  },
  advertiserName: {
    fontSize: 13,
    marginLeft: 6,
    marginRight: "auto",
    fontWeight: 500,
    color: "black",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cashbackRowContainer: {
    marginTop: 2,
    marginBottom: 13,
    paddingLeft: 10,
    paddingRight: 10,
  },
  termsButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 22,
    height: 22,
    padding: 0,
    border: "1px solid #E6E6E6",
    borderRadius: "50%",
    backgroundColor: theme.palette.white,
    color: theme.palette.black,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: "20px",
    cursor: "pointer",
    transition: "background-color 0.2s ease-out, border-color 0.2s ease-out",
    "&:hover": {
      backgroundColor: theme.palette.colors.background.lightGrey,
      borderColor: theme.palette.colors.border.light,
    },
    "&:focus": {
      outline: "none",
      borderColor: theme.palette.black,
    },
  },
  heartIconContainer: {
    position: "relative",
    left: "-10px",
    bottom: 171,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 22,
    height: 22,
  },
}));

export function GridCardView({
  offer,
  loggedIn,
  favourited,
  onRedirectClicked,
  onTermsModalClicked,
  onSignUpModalClicked,
}: OfferCardProps) {
  const classes: any = useStyles({});
  const { t } = useTranslation("common");
  const localStorageLanguage: string = localStorage.getItem("i18nextLng");

  const [animation, setAnimation] = useState<boolean>(false);
  const [termsVisible, setTermsVisible] = useState<boolean>(false);

  const handleMouseHover = (): void => setTermsVisible(!termsVisible);

  const handleTermsButtonClick = (event: MouseEvent<HTMLElement>): void => {
    if (event.button === 0) {
      event.stopPropagation();
      onTermsModalClicked(offer);
    }
  };

  const handleCardClick = (event: MouseEvent<HTMLInputElement>): void => {
    if (event.button === 0) {
      loggedIn ? handleCardPressed() : onSignUpModalClicked(offer);
    }
  };

  const handleCardPressed = (): void => {
    setAnimation(true);
    onRedirectClicked(offer);
    setTimeout(() => setAnimation(false), 120);
  };

  const payoutTextLocalized: string =
    localStorageLanguage === "fr" && offer.payoutText.includes("$")
      ? `${offer.payoutText.substring(1)}$`
      : offer.payoutText;

  const detailsVisible = loggedIn && termsVisible;

  const details = detailsVisible && (
    <button
      type="button"
      aria-label={t("details")}
      title={t("details")}
      onMouseDown={handleTermsButtonClick}
      className={classes.termsButton}
    >
      i
    </button>
  );

  return (
    <Card className={`${classes.card} ${animation && classes.cardPressed}`}>
      <CardContent
        onMouseEnter={() => !termsVisible && handleMouseHover()}
        onMouseLeave={() => termsVisible && handleMouseHover()}
        onMouseDown={handleCardClick}
        className={classes.cardContent}
      >
        <Grid
          container
          item
          justifyContent="center"
          alignItems="center"
          className={classes.brandInfoContainer}
        >
          <Grid
            container
            item
            direction="row-reverse"
            justifyContent="flex-start"
            alignItems="flex-start"
            className={classes.brandImageContainer}
          >
            <Image
              image={offer.backgroundImage}
              className={classes.brandImage}
              alt={`${offer.id}-brand-background`}
            />
          </Grid>
          <Grid
            container
            item
            direction="column"
            justifyContent="flex-start"
            className={classes.bottomContainer}
          >
            <Grid
              container
              item
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              className={classes.brandLogoRow}
            >
              <Grid
                container
                item
                justifyContent="center"
                alignItems="center"
                className={classes.brandLogoContainer}
              >
                <Image
                  image={offer.advertiserLogoImage}
                  className={classes.brandLogo}
                  alt={`${offer.id}-brand-logo`}
                />
              </Grid>
              <Grid item className={classes.brandNameContainer}>
                <div title={offer.advertiserName}>
                  <Typography className={classes.advertiserName}>
                    {offer.advertiserName}
                  </Typography>
                </div>
              </Grid>
            </Grid>
            <Grid
              container
              item
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              className={classes.cashbackRowContainer}
            >
              <PayoutBanner payoutText={payoutTextLocalized} smallPhone />
              <Grid>{details}</Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
